import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Briefcase,
  Users,
  TrendingUp,
  Shield,
  Sparkles,
  MessageSquare,
  Award,
  RotateCcw,
  Heart as HeartIcon,
  Baby,
  Clock,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { FeaturedJobs } from "./home/FeaturedJobs";
import { InsightHighlights } from "./home/InsightHighlights";
import { fetchJobs } from "../api/jobs";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { isEmployerVerified } from "../lib/jobVerification";

const heroFeatures = [
  {
    icon: Sparkles,
    title: "AI-Powered Matching",
    description: "Smart algorithms that prioritise inclusive hiring signals.",
  },
  {
    icon: MessageSquare,
    title: "Secure Messaging",
    description: "Employer-candidate chat with moderation and PII guardrails.",
  },
  {
    icon: Award,
    title: "Skill Verification",
    description: "Role-specific skill tests benchmarked across industry data.",
  },
  {
    icon: TrendingUp,
    title: "Career Insights",
    description: "Return-to-work pathways, salary telemetry, commute insights.",
  },
];

const quickFilters = [
  { id: "all", label: "All roles" },
  { id: "returner", label: "Returner friendly" },
  { id: "remote", label: "Remote first" },
  { id: "verified", label: "Verified employers" },
] as const;

export function Home() {
  const [activeFilter, setActiveFilter] = useState<(typeof quickFilters)[number]["id"]>("all");

  const isMobile = useMediaQuery("(max-width: 768px)");

  const jobsQuery = useQuery({
    queryKey: ["home-jobs"],
    queryFn: () => fetchJobs({}),
    staleTime: 1000 * 60,
  });
  const allJobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  const filteredJobs = useMemo(() => {
    let results = allJobs.slice();

    if (activeFilter === "returner") {
      results = results.filter((job) => job.returnerFriendly);
    } else if (activeFilter === "remote") {
      results = results.filter((job) => job.remote);
    } else if (activeFilter === "verified") {
      results = results.filter((job) => isEmployerVerified(job));
    }

    return results;
  }, [allJobs, activeFilter]);

  const featuredJobs = filteredJobs.slice(0, isMobile ? 4 : 6);

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://jobs.lighthouse.careers";
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Lighthouse Careers",
    url: siteUrl,
    description:
      "Lighthouse connects returners and inclusive employers through curated jobs, AI-powered matching, and talent services.",
    sameAs: ["https://www.linkedin.com/company/lighthouse", "https://twitter.com/lighthousejobs"],
  };

  return (
    <div className="space-y-20 pb-24">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#edc967]/10 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <div className="space-y-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#d2ac47]/40 bg-[#ba9036]/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#f7ef8a]">
                Career comeback ready
              </div>
              <div className="space-y-6">
                <h1 className="bg-gold-gradient bg-clip-text text-4xl font-semibold leading-tight text-transparent sm:text-5xl lg:text-6xl">
                  The job board built for returners, hiring teams, and inclusive growth
                </h1>
                <p className="text-lg text-gray-300 sm:text-xl">
                  Curated matches, verified employers, and tools to help every candidate
                  navigate their next chapter with confidence.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-start sm:text-left">
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-white/10 text-white hover:bg-white/20 sm:w-auto"
                >
                  <Link to="/candidate">
                    <Users className="mr-2 h-5 w-5" aria-hidden />
                    I&apos;m returning to work
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-gold-gradient text-black hover:brightness-110 sm:w-auto"
                >
                  <Link to="/employer">
                    <Briefcase className="mr-2 h-5 w-5" aria-hidden />
                    I&apos;m hiring
                  </Link>
                </Button>
              </div>
            </div>

            <div className="hidden h-full flex-col justify-between gap-6 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-left shadow-[0_45px_85px_-65px_rgba(12,12,30,0.65)] lg:flex">
              <div className="space-y-6">
                <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-400">
                  Platform promises
                </p>
                <ul className="space-y-4 text-sm text-gray-300">
                  <li className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-[#ba9036]" aria-hidden />
                    Every employer manually verified to protect candidates.
                  </li>
                  <li className="flex items-center gap-3">
                    <Sparkles className="h-5 w-5 text-[#ba9036]" aria-hidden />
                    AI assistant to personalise job matches and interview prep.
                  </li>
                  <li className="flex items-center gap-3">
                    <HeartIcon className="h-5 w-5 text-[#ba9036]" aria-hidden />
                    Returner programmes, mentorship, and wellbeing support baked in.
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-[#d2ac47]/20 bg-[#ba9036]/10 p-6 text-sm text-[#f7ef8a]">
                “The Lighthouse team provided the right blend of empathy, data, and speed.
                Our engineering returner cohort has a 96% retention rate.”
                <p className="mt-4 font-semibold text-[#f7ef8a]">— Priya, CTO @ Northstar Labs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="bg-gradient-to-b from-transparent to-white/5 py-16">
        <div className="mx-auto max-w-7xl space-y-12 px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-semibold text-white sm:text-4xl">
            Why Lighthouse outperforms legacy job boards
          </h2>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {heroFeatures.map(({ icon: Icon, title, description }) => (
              <Card
                key={title}
                className="group border-white/10 bg-white/[0.03] transition-all hover:-translate-y-1 hover:border-[#d2ac47]/30 hover:bg-white/[0.07]"
              >
                <CardContent className="space-y-4 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold-gradient text-black shadow-lg shadow-[#ba9036]/25">
                    <Icon className="h-6 w-6" aria-hidden />
                  </span>
                  <h3 className="text-lg font-semibold text-white">{title}</h3>
                  <p className="text-sm text-gray-300">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <InsightHighlights />
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="space-y-8 bg-[#0b0d1b] py-16">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-3xl font-semibold text-white sm:text-4xl">Roles curated for you</h2>
              <p className="text-sm text-gray-400">
                Filtered from {allJobs.length}+ live roles across premium verified employers.
              </p>
            </div>
            <div className="flex flex-wrap justify-start gap-2">
              {quickFilters.map((filter) => (
                <Badge
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`cursor-pointer border ${
                    activeFilter === filter.id
                      ? "border-[#d2ac47] bg-[#ba9036]/20 text-[#f7ef8a]"
                      : "border-white/10 bg-white/5 text-gray-200 hover:border-[#d2ac47]/30 hover:text-[#f7ef8a]"
                  }`}
                >
                  {filter.label}
                </Badge>
              ))}
            </div>
          </div>

          {featuredJobs.length ? (
            <FeaturedJobs jobs={featuredJobs} variant={isMobile ? "carousel" : "grid"} />
          ) : (
            <Card className="border-white/10 bg-white/[0.02]">
              <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
                <Sparkles className="h-8 w-8 text-[#ba9036]" aria-hidden />
                <p className="text-lg text-gray-300">
                  We&apos;re calibrating matches for your search. Try adjusting filters or saving the search to be
                  notified.
                </p>
                <Button variant="outline" className="border-[#d2ac47]/40 text-[#f7ef8a] hover:bg-[#ba9036]/10">
                  Save this search
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* For Employers Section */}
      <section className="bg-white/5 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-semibold text-white sm:text-4xl">Built for hiring teams</h2>
              <p className="mb-6 text-gray-300">
                Supercharge your talent pipeline with verified candidates, inclusive filters, and collaborative tools.
              </p>
              <ul className="space-y-3 text-gray-300">
                {[
                  "AI-assisted job specs co-authored with DE&I prompts.",
                  "Talent pools and outreach sequences optimised for returners.",
                  "Skill tests auto-assigned with RAG scoring and coaching tips.",
                  "Team dashboards with actionable insights, not vanity metrics.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-[#ae8625] to-[#f7ef8a]">
                      <Shield className="h-3 w-3 text-[#101225]" aria-hidden />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="bg-gold-gradient text-black">
                  <Link to="/employer">Start hiring today</Link>
                </Button>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" asChild>
                  <Link to="/subscription">Compare plans</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#edc967]/20 to-transparent border border-white/10 p-8">
                <div className="w-full h-full rounded-xl bg-white/5 backdrop-blur-sm flex items-center justify-center">
                  <Briefcase className="w-32 h-32 text-[#ba9036]/50" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Candidates Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#edc967]/20 to-transparent border border-white/10 p-8">
                <div className="w-full h-full rounded-xl bg-white/5 backdrop-blur-sm flex items-center justify-center">
                  <Users className="w-32 h-32 text-[#ba9036]/50" />
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="mb-6 text-gold-gradient">
                For Candidates
              </h2>
              <p className="text-gray-300 mb-6">
                Discover your dream job, build your career path, and connect with top employers.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'AI-generated CV builder',
                  'Personalized job recommendations',
                  'Career path guidance and training',
                  'Interview preparation assistant',
                  'Salary and commute estimators',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Shield className="w-3 h-3 text-black" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className="bg-gold-gradient hover:brightness-110 text-black">
                <Link to="/candidate">
                  Find Your Dream Job
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Returner Hub Section */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#ba9036]/10 border border-[#d2ac47]/20 rounded-full px-4 py-2 mb-6">
              <RotateCcw className="w-4 h-4 text-[#ba9036]" />
              <span className="text-sm text-[#ba9036]">New Feature</span>
            </div>
            <h2 className="mb-4 text-gold-gradient">
              Returner Hub
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Dedicated support for parents, carers, and long-term unemployed individuals returning to work. 
              Your career break is a strength, not a setback.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: Baby,
                title: 'Parents Returning',
                description: 'Flexible roles and family-friendly employers',
                stat: '3,200+ jobs'
              },
              {
                icon: HeartIcon,
                title: 'Carers Returning',
                description: 'Understanding employers with flexible arrangements',
                stat: '2,800+ jobs'
              },
              {
                icon: Clock,
                title: 'Long-Term Unemployed',
                description: 'Training programs and supportive pathways',
                stat: '4,500+ jobs'
              }
            ].map((path, index) => {
              const Icon = path.icon;
              return (
                <Card
                  key={index}
                  className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 group"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-black" />
                    </div>
                    <h3 className="mb-2 text-white">{path.title}</h3>
                    <p className="text-sm text-gray-400 mb-3">{path.description}</p>
                    <div className="text-[#ba9036]">{path.stat}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button 
              asChild
              size="lg"
              className="bg-gold-gradient hover:brightness-110 text-black"
            >
              <Link to="/returner-hub">
                <RotateCcw className="w-5 h-5 mr-2" />
                Explore Returner Hub
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="mb-4 text-white">For Candidates</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/candidate" className="hover:text-[#ba9036]">Browse Jobs</Link></li>
                <li><Link to="/candidate" className="hover:text-[#ba9036]">Career Advice</Link></li>
                <li><Link to="/candidate" className="hover:text-[#ba9036]">CV Builder</Link></li>
                <li><Link to="/returner-hub" className="hover:text-[#ba9036]">Returner Hub</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-white">For Employers</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/employer" className="hover:text-[#ba9036]">Post a Job</Link></li>
                <li><Link to="/employer" className="hover:text-[#ba9036]">Search Candidates</Link></li>
                <li><Link to="/subscription" className="hover:text-[#ba9036]">Subscriptions</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-white">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/help" className="hover:text-[#ba9036]">Help Center</Link></li>
                <li><Link to="/help" className="hover:text-[#ba9036]">FAQ</Link></li>
                <li><Link to="/help" className="hover:text-[#ba9036]">Contact Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-white">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/life" className="hover:text-[#ba9036]">About Lighthouse</Link></li>
                <li><Link to="/privacy" className="hover:text-[#ba9036]">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#ba9036]">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-[#ba9036]">Cookie Notice</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-400">
            <p>© 2025 Lighthouse Jobs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
